<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue'

import SourceHint from '~/components/ui/SourceHint.vue'

defineOptions({ inheritAttrs: false })

type Size = 'xsmall' | 'small' | 'medium'
type Theme = 'light' | 'dark'

type Props = {
  label: string
  icon?: string
  theme?: Theme
  size?: Size
  disabled?: boolean
  readonly?: boolean
  type?: string
  placeholder?: string
  modelValue?: string
  error?: string
  /** Texte d’aide sous le champ (ignoré si `error` est renseigné). */
  helper?: string
  maxlength?: number
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  size: 'small',
  disabled: false,
  readonly: false,
  type: 'text',
  placeholder: 'Placeholder',
  modelValue: '',
  icon: undefined,
  error: '',
  helper: '',
  maxlength: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const attrs = useAttrs()

const inputValue = ref(props.modelValue)

watch(
    () => props.modelValue,
    (newVal) => {
      inputValue.value = newVal ?? ''
    }
)

const updateValue = (event: Event) => {
  const val = (event.target as HTMLInputElement).value
  inputValue.value = val
  emit('update:modelValue', val)
}

const convertLabelToId = computed(() =>
    props.label.replace(/\s+/g, '_').toLowerCase()
)

const labelParts = computed(() => {
  const starIndex = props.label.indexOf('*')
  if (starIndex === -1) {
    return { before: props.label, hasRequiredStar: false, after: '' }
  }

  return {
    before: props.label.slice(0, starIndex),
    hasRequiredStar: true,
    after: props.label.slice(starIndex + 1),
  }
})

const hintId = computed(() => `${convertLabelToId.value}-hint`)
const hintText = computed(() => (props.error?.trim() ? props.error.trim() : (props.helper ?? '').trim()))
const describedBy = computed(() => (hintText.value ? hintId.value : undefined))
</script>

<template>
  <div class="input-container" :class="[size, { 'has-error': error }]">
    <label :for="convertLabelToId" class="input-label">
      <span v-if="icon" class="material-symbols-rounded input-label__icon" aria-hidden="true">
        {{ icon }}
      </span>
      <span>
        {{ labelParts.before }}<span v-if="labelParts.hasRequiredStar" class="required-star">*</span>{{ labelParts.after }}
      </span>
    </label>

    <input
        :id="convertLabelToId"
        :name="convertLabelToId"
        v-bind="attrs"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :value="inputValue"
        :aria-invalid="!!error"
        :aria-describedby="describedBy"
        @input="updateValue"
    >
    <SourceHint
      v-if="hintText"
      :hint-id="hintId"
      :text="hintText"
      :state="error ? 'error' : 'default'"
    />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/partials/elpdl/spacing" as *;
@use "~/assets/scss/app" as *;
@use "~/assets/scss/partials/components/source-field" as field;

.input-container {
  display: flex;
  flex-direction: column;
  justify-items: stretch;
  gap: $spacing-2xs;

  .input-label {
    display: inline-flex;
    align-items: center;
    gap: $spacing-2xs;
    color: var(--color-text-secondary);
  }

  .input-label__icon {
    font-size: 18px;
    line-height: 1;
  }

  .required-star {
    color: c.$enedis-error-500;
  }

  input {
    box-sizing: border-box;
    min-width: 224px;
    border: 1px solid var(--color-border-input);
    border-radius: 4px;
    background-color: var(--color-bg-input);
    @include field.source-field-interactions;

    &:read-only {
      background-color: c.$enedis-neutral-100;
      outline: none;
      border: none;
    }
  }

  &.xsmall {
    label { @extend .text-2xs; }
    input { @extend .text-2xs; padding: $spacing-2xs $spacing-xs; }
  }

  &.small {
    label { @extend .text-2xs; border-radius: 4px; }
    input { @extend .text-xs; padding: $spacing-2xs $spacing-xs; }
  }

  &.medium {
    label { @extend .text-xs; border-radius: 4px; }
    input { @extend .text-s; padding: $spacing-2xs $spacing-xs; }
  }

  &.has-error input {
    border-color: var(--color-danger);
  }
}
</style>
